import type { ChatInsightItem, ChatItem, ChatMessageGroup } from "./types";

/**
 * Per-turn render-config shape consumed by visibility predicates and the
 * render path. Plumbed as a single object so memoized subtrees observe
 * stable references.
 */
export type GroupRenderConfig = {
  readonly traceMode: string;
  readonly stageTransition: string;
  readonly loadingVariant: string;
  readonly loadingDelayMs: number;
  readonly stepHeight: string;
  readonly scrollSnap: boolean;
  readonly stageTransitionEnabled: boolean;
};

/**
 * Exact subset of `GroupRenderConfig` that visibility predicates care about.
 * Narrowed so callers in memo/effect deps can depend on just these primitives
 * instead of the whole config object's identity.
 */
export type VisibilityConfig = Pick<
  GroupRenderConfig,
  "traceMode" | "stageTransition" | "loadingVariant"
>;

export function isInferenceInsight(child: ChatItem): boolean {
  return child.kind === "insight" && (child as ChatInsightItem).variant === "inference";
}

/**
 * Mirrors the definite-null branches in ChatFeed's `renderItem` /
 * `renderTraceGroup`. When every item in a group is hidden, the outer
 * screen-height wrapper in ChatFeed would otherwise render as a blank
 * full-viewport step — so this predicate drives a group-level filter so
 * those wrappers never mount.
 *
 * Conservative: only covers items whose null-return is provable from props
 * alone. Widgets/text/recap/etc. may return null internally (e.g. a widget
 * component returning null under a config toggle) but are treated as
 * visible here — those cases are handled by their own grouping rules (e.g.
 * `noBoundary` in `CHAT_TOOL_CONFIG`) so they don't form solo groups.
 */
export function isItemHidden(
  item: ChatItem,
  config: VisibilityConfig,
  dismissedTransitionIds: ReadonlySet<string>,
): boolean {
  if (item.kind === "transition") {
    if (dismissedTransitionIds.has(item.id)) return true;
    if (config.stageTransition === "disabled") return true;
    return false;
  }
  if (config.traceMode === "disabled") {
    if (item.kind === "status" || item.kind === "insight" || item.kind === "trace-group") {
      return true;
    }
  }
  if (item.kind === "trace-group") {
    const children =
      config.traceMode === "plan"
        ? item.children.filter((c) => !isInferenceInsight(c))
        : item.children;
    if (children.length === 0) return true;
  }
  if (item.kind === "thinking" && config.loadingVariant === "disabled") return true;
  if (item.kind === "claim-disclaimer" && item.historic) return true;
  return false;
}

/**
 * Does this group have at least one item (inline or footer) that would
 * actually render under the current config / dismissal set? Used both by
 * the group-level filter in ChatFeed and by the transition-gate hook to
 * decide when a clicked transition card can dismiss.
 */
export function groupHasVisibleContent(
  group: ChatMessageGroup,
  config: VisibilityConfig,
  dismissedTransitionIds: ReadonlySet<string>,
): boolean {
  const anyItem = group.items.some((item) => !isItemHidden(item, config, dismissedTransitionIds));
  if (anyItem) return true;
  return (
    group.footerItems?.some((item) => !isItemHidden(item, config, dismissedTransitionIds)) ?? false
  );
}
